import { gql } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { PlotRoutes } from 'Routes';
import { IconLinearNote2 } from 'components/icons/components/linear/IconLinearNote2';
import {
  AttachmentFragmentAttachmentPreviewFragmentDoc,
  PostFragmentJuiceboxNoteSearchItemFragment,
  PostFragmentPostPreviewFragmentDoc,
  useCreateSearchHitForJuiceboxFilterSuggestionViewMutation,
} from 'graphql/generated';
import { ForwardedRef, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { theme } from 'styles/theme';

// eslint-disable-next-line
gql`
  fragment PostFragmentJuiceboxNoteSearchItem on PostModel {
    id
    title
    type
    ...PostFragmentPostPreview
  }
  ${PostFragmentPostPreviewFragmentDoc}
  ${AttachmentFragmentAttachmentPreviewFragmentDoc}
`;

interface Props {
  note: PostFragmentJuiceboxNoteSearchItemFragment;
  query?: string;
}

export const JuiceboxNoteSearchItem = forwardRef(
  ({ note, query }: Props, ref: ForwardedRef<HTMLElement>) => {
    const [createNoteSearchHit] =
      useCreateSearchHitForJuiceboxFilterSuggestionViewMutation();
    const location = useLocation();
    return (
      <Link
        to={PlotRoutes.juiceboxNote({ id: note.id })}
        state={{
          backgroundLocation: location,
        }}
      >
        <Box
          tabIndex={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 3,
            padding: theme.spacing(2, 3),
            ':hover': {
              backgroundColor: theme.colors?.utility[400],
            },
            '&:focus': {
              backgroundColor: theme.colors?.utility[400],
              outline: 'none',
            },
          }}
          ref={ref}
          onClick={() => {
            createNoteSearchHit({
              variables: {
                data: {
                  post: {
                    id: note.id,
                  },
                  query,
                },
              },
            });
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                backgroundColor: 'rgba(35, 6, 3, 0.05)',
                borderRadius: 1,
              }}
            >
              <IconLinearNote2 size={14} color={theme.colors?.primary.black} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography
                variant="subhead-lg"
                color={theme.colors?.primary.black}
              >
                {note.title}
              </Typography>
              <Typography
                variant="subhead-sm"
                color={theme.colors?.utility[800]}
              >
                Note
              </Typography>
            </Box>
          </Box>
        </Box>
      </Link>
    );
  },
);
